// WHAT: Progress Ring Component (Atom)
// WHY:  Circular progress indicator with customizable size, color, and center content
// DOMAIN: Health → Yazio
// DEPENDENCIES: Shared utils, ProgressRingProps type
// RISKS:
//   - NOTE: SVG rotation (-90°) required to start progress from top
//   - NOTE: Percentage is clamped to 0-100 range implicitly (strokeDashoffset calculation)
//   - NOTE: Color options are limited (green, yellow, red, blue)

import type { ProgressRingProperties } from "../../types";

import { cn } from "@/shared/utils";

/**
 * WHAT:
 *   Circular progress ring component with customizable appearance and center content.
 *
 * WHY:
 *   Provides visual progress representation for goals (calories, macros, etc.)
 *   with smooth animations and flexible styling.
 *
 * PROPS:
 *   - percentage: number — Progress percentage (0-100)
 *   - size?: number — Ring diameter in pixels (default: 120)
 *   - strokeWidth?: number — Stroke width in pixels (default: 8)
 *   - color?: 'green' | 'yellow' | 'red' | 'blue' — Progress color (default: 'green')
 *   - className?: string — Additional CSS classes
 *   - children?: ReactNode — Content to display in center of ring
 *
 * BEHAVIOR:
 *   - Calculates circle radius and circumference
 *   - Uses stroke-dasharray and stroke-dashoffset for progress effect
 *   - Rotates SVG -90° to start progress from top
 *   - Shows background circle (gray) and progress circle (colored)
 *   - Applies CSS transition for smooth animation (300ms ease-in-out)
 *   - Centers children content absolutely within ring
 *
 * RISKS:
 *   - NOTE: Percentage values > 100 or < 0 may cause visual issues
 *   - NOTE: Color options are limited (no custom colors)
 *   - NOTE: SVG rotation may cause layout issues in some contexts
 *
 * @param root0
 *
 * @param root0.percentage
 *
 * @param root0.size
 *
 * @param root0.strokeWidth
 *
 * @param root0.color
 *
 * @param root0.className
 *
 * @param root0.children
 *
 * @returns {JSX.Element} Progress ring component with customizable appearance
 *
 */
export function ProgressRing({
  children,
  className,
  color = "green",
  percentage,
  size = 120,
  strokeWidth = 8,
}: Readonly<ProgressRingProperties>) {
  const ringSize: number = size;
  const ringStrokeWidth: number = strokeWidth;
  const radius: number = (ringSize - ringStrokeWidth) / 2;
  const circumference: number = radius * 2 * Math.PI;
  const strokeDasharray: number = circumference;
  const strokeDashoffset: number = circumference - (percentage / 100) * circumference;

  const getColorClasses = () => {
    switch (color) {
      case "green": {
        return "stroke-green-500";
      }
      case "yellow": {
        return "stroke-yellow-500";
      }
      case "red": {
        return "stroke-red-500";
      }
      case "blue": {
        return "stroke-blue-500";
      }
      default: {
        return "stroke-green-500";
      }
    }
  };

  const safeClassName: string | undefined = className ?? undefined;

  return (
    <div className={cn("relative inline-flex items-center justify-center", safeClassName)}>
      <svg className="transform -rotate-90" height={ringSize} width={ringSize}>
        {/* Background circle */}
        <circle
          className="text-slate-700"
          cx={ringSize / 2}
          cy={ringSize / 2}
          fill="none"
          r={radius}
          stroke="currentColor"
          strokeWidth={ringStrokeWidth}
        />
        {/* Progress circle */}
        <circle
          className={cn("transition-all duration-300 ease-in-out", getColorClasses())}
          cx={ringSize / 2}
          cy={ringSize / 2}
          fill="none"
          r={radius}
          stroke="currentColor"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          strokeWidth={ringStrokeWidth}
        />
      </svg>
      {children ? (
        <div className="absolute inset-0 flex items-center justify-center">{children}</div>
      ) : null}
    </div>
  );
}
